import { useAppSelector } from "@/hooks/useReduxHooks";
import { selectUser } from "@/store/slices/user.slice";
import { useGetSidebarPersmissionsQuery } from "@/services/(my-space)/userApi";
import {
  useGetMyTimeSavedQuery,
  useGetTokenUsageQuery,
} from "@/services/(company)/subscriptionApi";
import { SECTIONS_ENUMS } from "@/utils/enums";

export interface SubscriptionSummary {
  isAdmin: boolean;
  hasSubscription: boolean;
  hasSubscriptionAccess: boolean;
  totalTimeSavedMinutes: number;
  tokenBalance: number;
  isTokensLoading: boolean;
}

/**
 * Derives the header's subscription-driven state (AI time-saving nudge,
 * time-saved widget, token upgrade widget) from the user + subscription APIs.
 */
export function useSubscriptionSummary(): SubscriptionSummary {
  const { user, isAdmin } = useAppSelector(selectUser);
  const { data: sidebarLinks } = useGetSidebarPersmissionsQuery();

  const hasSubscription = Boolean(user?.company?.companySubscription);
  const hasSubscriptionAccess =
    sidebarLinks?.data?.company?.some(
      (link) => link.name === SECTIONS_ENUMS.SUBSCRIPTION,
    ) ?? false;

  const { data: myTimeSavedData } = useGetMyTimeSavedQuery(
    { period: "1M" },
    { skip: !hasSubscription },
  );
  const totalTimeSavedMinutes =
    myTimeSavedData?.data?.totalTimeSavedMinutes ?? 0;

  // Token balance only feeds the admin upgrade widget — skip the fetch
  // entirely when it can't be shown.
  const needsTokenBalance = isAdmin && hasSubscription && hasSubscriptionAccess;
  const {
    data: tokenData,
    isLoading,
    isFetching,
  } = useGetTokenUsageQuery(undefined, { skip: !needsTokenBalance });
  const tokens = tokenData?.data;
  const tokenBalance =
    (tokens?.totalAllocated ?? 0) - (tokens?.totalConsumed ?? 0);

  return {
    isAdmin,
    hasSubscription,
    hasSubscriptionAccess,
    totalTimeSavedMinutes,
    tokenBalance,
    isTokensLoading: isLoading || isFetching,
  };
}
